// frontend/services/api.js
// Normalize base URL: ensure no trailing slash and no trailing '/api'
// Base URL (can be overridden by env). Keep original value then normalize.
let BASE_URL_RAW = process.env.NEXT_PUBLIC_API_URL || 'https://ktr.investments/api';

// In browser/dev, prefer a local backend if present to avoid hitting production host accidentally.
if (typeof window !== 'undefined') {
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (isLocalhost) {
    // Allow explicit override for local dev: NEXT_PUBLIC_API_URL_DEV
    BASE_URL_RAW = process.env.NEXT_PUBLIC_API_URL_DEV || 'http://localhost:5002/api';
  }
}

let BASE_URL = BASE_URL_RAW;
// Remove trailing slash
BASE_URL = BASE_URL.replace(/\/+$/, '');
// If the provided base ends with '/api', strip it so we can consistently add '/api' when building paths
if (BASE_URL.toLowerCase().endsWith('/api')) {
  BASE_URL = BASE_URL.slice(0, -4);
}

function getJwt() {
  try { return localStorage.getItem('jwt') || ''; } catch { return ''; }
}

async function apiFetch(path, { method = 'GET', headers = {}, body } = {}) {
  const h = { Accept: 'application/json', ...headers };
  const jwt = getJwt();
  if (jwt && !('Authorization' in h)) h.Authorization = `Bearer ${jwt}`;

  // Build URL ensuring a single '/api' prefix and a single slash between segments
  const cleanedPath = path.startsWith('/') ? path : `/${path}`;
  const apiPrefix = '/api';
  const url = `${BASE_URL}${apiPrefix}${cleanedPath}`.replace(/([^:]\/\/)\/+/g, '$1');
  const res = await fetch(url, { method, headers: h, body });

  const ct = res.headers.get('content-type') || '';
  const data = ct.includes('application/json') ? await res.json() : { message: await res.text() };

  if (!res.ok) {
    const msg =
      (Array.isArray(data.errors) && data.errors.join('; ')) ||
      data.message || data.error || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

// ========= AUTH =========
export async function signupUser({ firstName, lastName, email, password }) {
  // Send snake_case keys to the API (server canonical)
  return apiFetch('/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ first_name: firstName, last_name: lastName, email, password }),
  });
}

export async function loginUser({ email, password }) {
  return apiFetch('/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
}

export async function authStatus() {
  return apiFetch('/auth/status', { method: 'GET' });
}

// ========= KYC =========
export async function kycStatus() {
  return apiFetch('/kyc', { method: 'GET' });
}

export async function kycSave(payload) {
  return apiFetch('/kyc/save', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function kycSubmit(payload) {
  return apiFetch('/kyc', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

// ========= AI (welcome) =========
export async function aiWelcome(body, extraHeaders = {}) {
  return apiFetch('/ai', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...extraHeaders },
    body: JSON.stringify(body),
  });
}
const API = process.env.NEXT_PUBLIC_API_URL || 'https://ktr.investments/api';

import { getAuthHeaders } from '../utils/auth';

/**
 * Minimal dashboard data fetch; safe on SSR.
 */
export async function getDashboardData() {
  try {
    const res = await fetch(`${API}/dashboard`, { headers: getAuthHeaders() });
    if (!res.ok) return { news: [], positions: [], account: {} };
    return await res.json();
  } catch {
    return { news: [], positions: [], account: {} };
  }
}

/**
 * Minimal market data fetch; safe fallback.
 */
export async function getMarketData(symbols = []) {
  try {
    const res = await fetch(`${API}/market/quotes`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ symbols }),
    });
    if (!res.ok) return [];
    return await res.json();
  } catch {
    return [];
  }
}
